<script setup>
import { ref, watch, nextTick } from 'vue';
import { ArrowUp, Square } from 'lucide-vue-next';

const props = defineProps({
  disabled: {
    type: Boolean,
    default: false,
  },
  loading: {
    type: Boolean,
    default: false,
  },
  placeholder: {
    type: String,
    default: 'Ask anything about companies, data, or insights...',
  },
});

const emit = defineEmits(['send', 'stop']);

const message = ref('');
const textareaRef = ref(null);

function adjustHeight() {
  nextTick(() => {
    if (textareaRef.value) {
      textareaRef.value.style.height = 'auto';
      const scrollHeight = textareaRef.value.scrollHeight;
      const maxHeight = 200;
      textareaRef.value.style.height = Math.min(scrollHeight, maxHeight) + 'px';
    }
  });
}

watch(message, adjustHeight);

function handleSubmit() {
  if (props.disabled || props.loading || !message.value.trim()) return;

  emit('send', message.value.trim());
  message.value = '';

  nextTick(() => {
    adjustHeight();
    textareaRef.value?.focus();
  });
}

function handleKeydown(e) {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    handleSubmit();
  }
}

function handleStop() {
  emit('stop');
}

function focusInput() {
  textareaRef.value?.focus();
}

defineExpose({ focusInput });
</script>

<template>
  <div class="shrink-0 pt-4 pb-8 bg-main-bg px-6">
    <div class="max-w-3xl mx-auto px-6">
      <div
        class="relative flex items-end gap-3 bg-input-bg border border-input-border rounded-2xl px-4 py-3 focus-within:border-text-muted/50 transition-colors"
      >
        <textarea
          ref="textareaRef"
          v-model="message"
          class="flex-1 bg-transparent text-text-primary placeholder-text-muted/60 resize-none outline-none text-[15px] leading-relaxed max-h-[200px] overflow-y-auto py-1"
          rows="1"
          :disabled="disabled"
          :placeholder="placeholder"
          @keydown="handleKeydown"
        />

        <button
          v-if="loading"
          class="shrink-0 w-8 h-8 rounded-lg bg-text-primary flex items-center justify-center hover:bg-text-secondary transition-colors"
          title="Stop generating"
          @click="handleStop"
        >
          <Square :size="12" class="text-main-bg" fill="currentColor" />
        </button>

        <button
          v-else
          :disabled="disabled || !message.trim()"
          class="shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200"
          :class="
            message.trim() && !disabled
              ? 'bg-text-primary hover:bg-text-secondary text-main-bg'
              : 'bg-text-muted/20 text-text-muted cursor-not-allowed'
          "
          title="Send message"
          @click="handleSubmit"
        >
          <ArrowUp :size="18" :stroke-width="2.5" />
        </button>
      </div>

      <p class="text-center text-xs text-text-muted/60 mt-3">
        Ciena AI can make mistakes. Consider checking important information.
      </p>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@tailwind base;
@tailwind components;
@tailwind utilities;
@import './../main.css';
</style>
